(() => {
  const config = {
    imageUrl: '',
    width: 84,
    height: 84,
    offsetX: 0,
    offsetY: -8,
    hideWhenChatOpen: true,
    zIndex: -1,
    mobileDockVisible: {
      enabled: false,
      maxWidth: 767,
      bodyHasSelector: '.header-sticky.header-hidden',
      left: '',
      bottom: '',
      width: null,
      height: null,
      zIndex: null,
    },
    mobileDockDefault: {
      enabled: false,
      maxWidth: 767,
      bodyMissingSelector: '.header-sticky.header-hidden',
      left: '',
      bottom: '',
      width: null,
      height: null,
      zIndex: null,
    },
    debug: true,
    ...window.IGLU_CHAT_OVERLAY,
  };

  if (!config.imageUrl) {
    return;
  }

  const OVERLAY_ID = 'iglu-chat-overlay';
  const SHADOW_OVERLAY_ID = 'iglu-chat-overlay-shadow';
  const SHADOW_STYLE_ID = 'iglu-chat-overlay-style';
  let overlay = null;
  let host = null;
  let rafToken = null;
  let hostObserver = null;
  let shadowObserver = null;
  let runtimeShadowObserver = null;
  let observedShadowRoot = null;
  let observedRuntimeShadowRoot = null;
  let documentObserver = null;
  let bodyObserver = null;
  let listenersAttached = false;
  let activatorButton = null;
  let retryTimer = null;
  let retryRemaining = 0;
  let wasChatOpen = false;
  let lastStateSnapshot = '';
  let hasLoggedMissingActivator = false;
  let lastRenderMode = '';
  const REATTACH_RETRY_COUNT = 12;
  const REATTACH_RETRY_DELAY_MS = 100;

  function log(event, details) {
    if (!config.debug) {
      return;
    }

    if (details !== undefined) {
      console.log('[IGLU Chat Overlay]', event, details);
      return;
    }

    console.log('[IGLU Chat Overlay]', event);
  }

  function findHost() {
    return document.querySelector('shopify-chat');
  }

  function findActivator(hostElement) {
    const hostShadowRoot = hostElement?.shadowRoot;
    if (!hostShadowRoot) {
      return null;
    }

    const directActivator = hostShadowRoot.querySelector('.activator-nudge');
    if (directActivator) {
      return directActivator;
    }

    const runtime = hostShadowRoot.querySelector('shopify-agent-runtime');
    return runtime?.shadowRoot?.querySelector('.activator-nudge') || null;
  }

  function findActivatorButton(hostElement) {
    const activator = findActivator(hostElement);
    if (!activator) {
      return null;
    }

    return activator.querySelector('button.activator, button[part="activator"]');
  }

  function getChatOpenState(hostElement) {
    if (!hostElement) {
      return false;
    }

    const hostOpen = hostElement.hasAttribute('open') || hostElement.getAttribute('is-open') === 'true';
    const runtime = hostElement.shadowRoot?.querySelector('shopify-agent-runtime');
    const runtimeOpen = Boolean(runtime && (runtime.hasAttribute('open') || runtime.getAttribute('open') === 'true'));
    const button = findActivatorButton(hostElement);
    const expanded = button?.getAttribute('aria-expanded') === 'true';

    return hostOpen || runtimeOpen || expanded;
  }

  function isVisible(element) {
    if (!element) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    const styles = window.getComputedStyle(element);

    if (styles.display === 'none' || styles.visibility === 'hidden' || Number(styles.opacity) === 0) {
      return false;
    }

    return rect.width > 0 && rect.height > 0;
  }

  function ensureOverlay() {
    if (overlay) {
      return overlay;
    }

    overlay = document.getElementById(OVERLAY_ID);

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      overlay.setAttribute('aria-hidden', 'true');

      const image = document.createElement('img');
      image.src = config.imageUrl;
      image.alt = '';
      image.decoding = 'async';
      image.loading = 'eager';
      overlay.appendChild(image);

      document.body.appendChild(overlay);
    }

    overlay.style.width = `${config.width}px`;
    overlay.style.height = `${config.height}px`;
    overlay.style.zIndex = String(config.zIndex);

    return overlay;
  }

  function ensureShadowStyle(shadowRoot) {
    if (!shadowRoot || shadowRoot.getElementById(SHADOW_STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = SHADOW_STYLE_ID;
    style.textContent = `
      #${SHADOW_OVERLAY_ID} {
        position: absolute;
        inset-inline-start: 50%;
        inset-block-end: calc(100% + var(--iglu-overlay-offset-y, 8px));
        transform: translateX(calc(-50% + var(--iglu-overlay-offset-x, 0px)));
        width: var(--iglu-overlay-width, 84px);
        height: var(--iglu-overlay-height, 84px);
        z-index: var(--iglu-overlay-z, 31);
        pointer-events: none;
        opacity: 0;
        transition: opacity var(--animation-primary, 0.2s ease), transform var(--animation-primary, 0.2s ease);
      }

      #${SHADOW_OVERLAY_ID}.is-visible {
        opacity: 1;
      }

      #${SHADOW_OVERLAY_ID} img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    `;

    shadowRoot.appendChild(style);
  }

  function ensureShadowOverlay(activator, width, height, zIndex) {
    if (!activator) {
      return null;
    }

    const shadowRoot = activator.getRootNode();
    ensureShadowStyle(shadowRoot);

    let shadowOverlay = shadowRoot.getElementById(SHADOW_OVERLAY_ID);
    if (!shadowOverlay) {
      shadowOverlay = document.createElement('span');
      shadowOverlay.id = SHADOW_OVERLAY_ID;
      shadowOverlay.setAttribute('aria-hidden', 'true');

      const image = document.createElement('img');
      image.src = config.imageUrl;
      image.alt = '';
      image.decoding = 'async';
      image.loading = 'eager';
      shadowOverlay.appendChild(image);

      if (window.getComputedStyle(activator).position === 'static') {
        activator.style.position = 'relative';
      }

      activator.appendChild(shadowOverlay);
    }

    shadowOverlay.style.setProperty('--iglu-overlay-width', `${width}px`);
    shadowOverlay.style.setProperty('--iglu-overlay-height', `${height}px`);
    shadowOverlay.style.setProperty('--iglu-overlay-z', String(zIndex));
    shadowOverlay.style.setProperty('--iglu-overlay-offset-x', `${config.offsetX || 0}px`);
    shadowOverlay.style.setProperty('--iglu-overlay-offset-y', `${config.offsetY ?? -8}px`);

    return shadowOverlay;
  }

  function getShadowOverlays(hostElement) {
    if (!hostElement?.shadowRoot) {
      return [];
    }

    const overlays = [];
    const hostOverlay = hostElement.shadowRoot.getElementById(SHADOW_OVERLAY_ID);
    if (hostOverlay) {
      overlays.push(hostOverlay);
    }

    const runtime = hostElement.shadowRoot.querySelector('shopify-agent-runtime');
    const runtimeOverlay = runtime?.shadowRoot?.getElementById(SHADOW_OVERLAY_ID);
    if (runtimeOverlay) {
      overlays.push(runtimeOverlay);
    }

    return overlays;
  }

  function setAllShadowOverlaysVisible(hostElement, visible) {
    const overlays = getShadowOverlays(hostElement);
    overlays.forEach((node) => {
      node.classList.toggle('is-visible', visible);
      node.style.display = visible ? '' : 'none';
      node.style.opacity = visible ? '' : '0';
    });
  }

  function isWithinMaxWidth(maxWidth) {
    return window.innerWidth <= Number(maxWidth || 767);
  }

  function bodyHasSelector(selector) {
    if (!selector) {
      return false;
    }

    return Boolean(document.body && document.body.querySelector(selector));
  }

  function getActiveMobileDockMode() {
    const visibleMode = config.mobileDockVisible;
    if (
      visibleMode
      && visibleMode.enabled
      && isWithinMaxWidth(visibleMode.maxWidth)
      && bodyHasSelector(visibleMode.bodyHasSelector)
    ) {
      return visibleMode;
    }

    const defaultMode = config.mobileDockDefault;
    if (!defaultMode || !defaultMode.enabled || !isWithinMaxWidth(defaultMode.maxWidth)) {
      return false;
    }

    const selector = defaultMode.bodyMissingSelector || (visibleMode && visibleMode.bodyHasSelector);
    if (selector && bodyHasSelector(selector)) {
      return false;
    }

    return defaultMode;
  }

  function applyFixedMode(overlayNode, modeConfig) {
    if (modeConfig.width != null) {
      overlayNode.style.width = `${modeConfig.width}px`;
    }

    if (modeConfig.height != null) {
      overlayNode.style.height = `${modeConfig.height}px`;
    }

    if (modeConfig.zIndex != null) {
      overlayNode.style.zIndex = String(modeConfig.zIndex);
    }

    if (modeConfig.left) {
      overlayNode.style.left = modeConfig.left;
    }

    if (modeConfig.bottom) {
      overlayNode.style.bottom = modeConfig.bottom;
    }
  }

  function scheduleUpdate() {
    if (rafToken) {
      return;
    }

    rafToken = window.requestAnimationFrame(() => {
      rafToken = null;
      updateOverlayPosition();
    });
  }

  function clearReattachRetries() {
    if (!retryTimer) {
      retryRemaining = 0;
      return;
    }

    window.clearTimeout(retryTimer);
    retryTimer = null;
    retryRemaining = 0;
  }

  function queueReattachRetries(count = REATTACH_RETRY_COUNT, options = {}) {
    const { restart = false, reason = '' } = options;

    if (retryTimer && !restart) {
      return;
    }

    clearReattachRetries();

    retryRemaining = count;
    log('Starting reattach retries', { count, delayMs: REATTACH_RETRY_DELAY_MS, reason });

    const tick = () => {
      if (retryRemaining <= 0) {
        retryTimer = null;
        retryRemaining = 0;
        log('Reattach retries finished');
        return;
      }

      retryRemaining -= 1;
      scheduleUpdate();
      retryTimer = window.setTimeout(tick, REATTACH_RETRY_DELAY_MS);
    };

    retryTimer = window.setTimeout(tick, REATTACH_RETRY_DELAY_MS);
  }

  function logRenderMode(nextMode, details) {
    if (nextMode === lastRenderMode) {
      return;
    }

    lastRenderMode = nextMode;
    log('Render mode', { mode: nextMode, ...details });
  }

  function disconnectHostObservers() {
    if (hostObserver) {
      hostObserver.disconnect();
      hostObserver = null;
    }

    if (shadowObserver) {
      shadowObserver.disconnect();
      shadowObserver = null;
    }

    if (runtimeShadowObserver) {
      runtimeShadowObserver.disconnect();
      runtimeShadowObserver = null;
    }

    if (activatorButton) {
      activatorButton.removeEventListener('click', onActivatorClick);
      activatorButton = null;
    }

    observedShadowRoot = null;
    observedRuntimeShadowRoot = null;
  }

  function onActivatorClick() {
    log('Activator clicked');
    queueReattachRetries(8, { restart: true, reason: 'activator-click' });
    scheduleUpdate();
  }

  function observeActivatorButton() {
    const nextButton = findActivatorButton(host);
    if (nextButton === activatorButton) {
      return;
    }

    if (activatorButton) {
      activatorButton.removeEventListener('click', onActivatorClick);
      activatorButton = null;
    }

    if (!nextButton) {
      return;
    }

    activatorButton = nextButton;
    activatorButton.addEventListener('click', onActivatorClick, { passive: true });
    log('Attached activator click listener');
  }

  function observeShadowRoot() {
    const nextShadowRoot = host?.shadowRoot || null;

    if (!nextShadowRoot) {
      if (shadowObserver) {
        shadowObserver.disconnect();
        shadowObserver = null;
      }
      observedShadowRoot = null;
      return;
    }

    if (observedShadowRoot === nextShadowRoot && shadowObserver) {
      return;
    }

    if (shadowObserver) {
      shadowObserver.disconnect();
    }

    observedShadowRoot = nextShadowRoot;
    log('Observing chat shadow root for mutations');
    shadowObserver = new MutationObserver(scheduleUpdate);
    shadowObserver.observe(nextShadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open', 'aria-expanded'],
    });
  }

  function observeRuntimeShadowRoot() {
    const runtimeShadowRoot = host?.shadowRoot?.querySelector('shopify-agent-runtime')?.shadowRoot || null;

    if (!runtimeShadowRoot) {
      if (runtimeShadowObserver) {
        runtimeShadowObserver.disconnect();
        runtimeShadowObserver = null;
      }
      observedRuntimeShadowRoot = null;
      return;
    }

    if (observedRuntimeShadowRoot === runtimeShadowRoot && runtimeShadowObserver) {
      return;
    }

    if (runtimeShadowObserver) {
      runtimeShadowObserver.disconnect();
    }

    observedRuntimeShadowRoot = runtimeShadowRoot;
    log('Observing runtime shadow root for mutations');
    runtimeShadowObserver = new MutationObserver(scheduleUpdate);
    runtimeShadowObserver.observe(runtimeShadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'open', 'aria-expanded'],
    });
  }

  function syncHostReference() {
    const nextHost = findHost();
    if (nextHost === host) {
      return;
    }

    disconnectHostObservers();
    host = nextHost;
    log(host ? 'Chat host detected or replaced' : 'Chat host missing');
    observeHost();
  }

  function updateOverlayPosition() {
    syncHostReference();

    if (!host) {
      if (overlay) {
        overlay.classList.remove('is-visible');
      }
      return;
    }

    observeActivatorButton();

    const isChatOpen = getChatOpenState(host);
    if (wasChatOpen && !isChatOpen) {
      // The widget may rebuild shadow content shortly after close.
      log('Chat closed; scheduling post-close reattach checks');
      hasLoggedMissingActivator = false;
      queueReattachRetries(REATTACH_RETRY_COUNT, { restart: true, reason: 'chat-closed' });
    }
    if (!wasChatOpen && isChatOpen) {
      log('Chat opened; overlay will hide while open');
    }
    wasChatOpen = isChatOpen;

    if (config.hideWhenChatOpen && isChatOpen) {
      setAllShadowOverlaysVisible(host, false);
      if (overlay) {
        overlay.classList.remove('is-visible');
      }
      return;
    }

    // Always mirror the host visibility state first so the overlay disappears
    // when the chat button is hidden by header/bottom conditions.
    if (!isVisible(host)) {
      setAllShadowOverlaysVisible(host, false);
      if (overlay) {
        overlay.classList.remove('is-visible');
      }
      return;
    }

    const mobileMode = getActiveMobileDockMode();
    const activator = findActivator(host);
    const stateSnapshot = [
      `open:${isChatOpen}`,
      `activator:${Boolean(activator)}`,
      `mobileMode:${Boolean(mobileMode)}`,
      `hostVisible:${isVisible(host)}`,
    ].join('|');
    if (stateSnapshot !== lastStateSnapshot) {
      lastStateSnapshot = stateSnapshot;
      log('State update', {
        open: isChatOpen,
        activatorFound: Boolean(activator),
        mobileModeActive: Boolean(mobileMode),
        hostVisible: isVisible(host),
      });
    }

    if (activator) {
      const width = mobileMode && mobileMode.width != null ? mobileMode.width : config.width;
      const height = mobileMode && mobileMode.height != null ? mobileMode.height : config.height;
      const zIndex = mobileMode && mobileMode.zIndex != null ? mobileMode.zIndex : config.zIndex;

      const shadowOverlay = ensureShadowOverlay(activator, width, height, zIndex);
      if (shadowOverlay && isVisible(activator)) {
        clearReattachRetries();
        hasLoggedMissingActivator = false;
        setAllShadowOverlaysVisible(host, false);
        logRenderMode('shadow', { width, height, zIndex });
        shadowOverlay.style.display = '';
        shadowOverlay.classList.add('is-visible');
        shadowOverlay.style.opacity = '';
        if (overlay) {
          overlay.classList.remove('is-visible');
        }
        return;
      }
    }

    if (!activator && !isChatOpen) {
      if (!hasLoggedMissingActivator) {
        log('Activator missing after close; waiting for shadow rerender');
        hasLoggedMissingActivator = true;
      }
      queueReattachRetries(6, { reason: 'activator-missing' });
    }

    const overlayNode = ensureOverlay();
    if (mobileMode) {
      applyFixedMode(overlayNode, mobileMode);
      logRenderMode('fallback-mobile', { mobileModeActive: true });
      overlayNode.classList.add('is-visible');
      return;
    }

    overlayNode.style.width = `${config.width}px`;
    overlayNode.style.height = `${config.height}px`;
    overlayNode.style.zIndex = String(config.zIndex);

    const rect = host.getBoundingClientRect();
    const left = rect.left + (rect.width / 2) - (config.width / 2) + config.offsetX;
    const bottom = rect.bottom - config.height + config.offsetY;

    overlayNode.style.left = `${Math.round(left)}px`;
    overlayNode.style.bottom = `${Math.round(bottom)}px`;
    logRenderMode('fallback-document', { left: Math.round(left), bottom: Math.round(bottom) });
    overlayNode.classList.add('is-visible');
  }

  function observeHost() {
    if (!host) {
      return;
    }

    if (!hostObserver) {
      log('Observing chat host attributes');
      hostObserver = new MutationObserver(scheduleUpdate);
      hostObserver.observe(host, {
        attributes: true,
        attributeFilter: ['class', 'style', 'is-open', 'open'],
      });
    }

    observeShadowRoot();
    observeRuntimeShadowRoot();
    observeActivatorButton();
  }

  function observeDocument() {
    if (!documentObserver) {
      log('Observing document for chat mount/unmount changes');
      documentObserver = new MutationObserver(() => {
        syncHostReference();
        observeHost();

        scheduleUpdate();
      });

      documentObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    if (document.body && !bodyObserver) {
      bodyObserver = new MutationObserver(scheduleUpdate);
      bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }
  }

  function attachListeners() {
    if (listenersAttached) {
      return;
    }

    listenersAttached = true;
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
  }

  function init() {
    syncHostReference();
    wasChatOpen = getChatOpenState(host);
    log('Overlay script initialized', {
      hostFound: Boolean(host),
      initiallyOpen: wasChatOpen,
      imageUrl: config.imageUrl,
      hideWhenChatOpen: config.hideWhenChatOpen,
    });
    observeDocument();
    attachListeners();
    scheduleUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
